import React, { createContext, useContext, useEffect, useState } from 'react';
import { ref, onValue, off } from 'firebase/database';
import { database } from '../firebase';
import { useUser } from './UserContext';

// Define your Event type here (customize as needed)
export interface Event {
  id: string;
  name: string;
  host?: string;
  hostName?: string; // Added for event host name
  title?: string; // Added for event title
  address?: string; // Added for event address
  startTime?: string; // <-- Added to ensure startTime is included
  date?: string;
  time?: string;
  location?: string;
  description?: string;
  private?: boolean;
  endTime?: string;
  eventType?: string;
  registerRequired?: boolean;
  website?: string;
  meetingID?: string;
  meetingPassword?: string;
  isPostToAll?: boolean;
  hasTarget?: boolean;
  eventTargetStr?: string;
  activityID?: string;
  eventCategory?: string | string[]; // Added for event category (supports both single and multiple)
  repostedBy?: string; // schoolID of the user who reposted
  repostedAt?: number; // timestamp when reposted
  schoolId?: string; // Added for Firebase path
  userId?: string; // Added for Firebase path
  postTime?: number; // Added for newsfeed filtering
  // Add other event fields as needed
}

interface EventsContextType {
  events: Event[];
  repostedEventsByCurrentUser: Event[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

const EventsContext = createContext<EventsContextType>({
  events: [],
  repostedEventsByCurrentUser: [],
  loading: true,
  error: null,
  refresh: () => {},
});

export const useEvents = () => useContext(EventsContext);

export const EventsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [repostedEventsByCurrentUser, setRepostedEventsByCurrentUser] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useUser();
  const [listenerCleanup, setListenerCleanup] = useState<(() => void) | null>(null);

  const fetchEvents = () => {
    // Clean up existing listener if it exists
    if (listenerCleanup) {
      listenerCleanup();
    }
    
    // Don't fetch events if user is not authenticated
    if (!user) {
      setLoading(false);
      setEvents([]);
      setRepostedEventsByCurrentUser([]);
      return () => {}; // Return empty cleanup function
    }
    
    setLoading(true);
    setError(null);
    const eventsRef = ref(database, 'schoolEvent');
    const listener = onValue(eventsRef, (snapshot) => {
      if (snapshot.exists()) {
        const schoolsData = snapshot.val();
        const allEvents: Event[] = [];
        const repostedEvents: Event[] = [];
        
        for (const schoolId in schoolsData) {
          const schoolEventsByUser = schoolsData[schoolId];
          for (const userId in schoolEventsByUser) {
            const userEvents = schoolEventsByUser[userId];
            for (const eventId in userEvents) {
              const eventData = userEvents[eventId];
              const eventContent = eventData?.eventContent;
              if (!eventContent) continue;
              
              // Add schoolId and userId to each event
              const event: Event = {
                id: eventId,
                ...eventContent,
                ...eventData, // <-- Add this line to include pollResult and other top-level fields
                repostedBy: eventData.repostedBy,
                repostedAt: eventData.repostedAt,
                postTime: eventData.postTime,
                schoolId,
                userId,
              };
              
              allEvents.push(event);
              
              // Check if this event has been reposted by the current user (repostedBy is an object keyed by schoolID)
              if (
                user?.schoolID &&
                event.repostedBy &&
                typeof event.repostedBy === 'object' &&
                Object.prototype.hasOwnProperty.call(event.repostedBy, user.schoolID)
              ) {
                // Optionally, extract the latest repostedAt timestamp for this schoolID
                const reposts = event.repostedBy[user.schoolID];
                let latestRepostTime = undefined;
                if (reposts && typeof reposts === 'object') {
                  const times = Object.values(reposts);
                  if (times.length > 0) {
                    latestRepostTime = Math.max(...times.map(Number));
                  }
                }
                repostedEvents.push({ ...event, repostedAt: latestRepostTime });
              }
            }
          }
        }
        
        setEvents(allEvents);
        setRepostedEventsByCurrentUser(repostedEvents);
      } else {
        setEvents([]);
        setRepostedEventsByCurrentUser([]);
      }
      setLoading(false);
    }, (err) => {
      setError('Failed to load events');
      setLoading(false);
    });
    
    const cleanup = () => off(eventsRef, 'value', listener);
    setListenerCleanup(() => cleanup);
    return cleanup;
  };

  useEffect(() => {
    const cleanup = fetchEvents();
    return cleanup;
  }, [user]); // Re-fetch when user authentication state changes

  const refresh = () => {
    fetchEvents();
  };

  return (
    <EventsContext.Provider value={{ 
      events, 
      repostedEventsByCurrentUser, 
      loading, 
      error, 
      refresh 
    }}>
      {children}
    </EventsContext.Provider>
  );
}; 